<template>
  <div class="markers-wrapper" v-click-outside="clearSelection" @keydown.esc="clearSelection" tabindex="0">
    <div v-if="statusDot" class="dot__status">
      x: <input class="form-control" type="number" v-model.number="statusDot.x"> y: <input class="form-control" type="number" v-model.number="statusDot.y">
    </div>

    <div 
      ref="canvasWrapper"
      :style="bgStyle"
    >
      <div v-for="item in propertyMarkers" :key="item._id">
        <div
          class="dot"
          :style="{
            left: item.marker.x * scale + 'px',
            top: item.marker.y * scale + 'px'
          }"
          @pointerdown="onMouseDown(item, $event)"
          @pointerup="onMouseUp($event)"
          @pointermove="onMouseDrag($event)"
        >
          <div class="dot__label">{{ item.label }}</div>
        </div>

        <polygon-shape 
          :polygon="item.polygon"
          :scale="scale"
          :isHighlighted="highlightedPolygon === item.polygon"
          :isSelected="selectedPolygon === item.polygon"
          :isTagSelected="selectedItem && selectedItem._id === item._id"
          @updatePolygon="item.polygon = $event"
          @selectedPolygon="polygonSelected($event)"
        />
      </div>

      <div v-for="item in subsectionGroupMarkers" :key="item._id">
        <tag-basic
          v-bind="item.marker"
          :pos="{ 
            x: item.marker.x,
            y: item.marker.y
          }"
          :label="{ en: item.label + '<br>now selling' }"
          :style="`transform: rotate(${item.marker.rotation}deg);`"
          @pointerdown.native="onMouseDown(item, $event)"
          @pointerup.native="onMouseUp($event)"
          @pointermove.native="onMouseDrag($event)"
        />

        <polygon-shape
          :polygon="item.polygon"
          :scale="scale"
          :isHighlighted="highlightedPolygon === item.polygon"
          :isSelected="selectedPolygon === item.polygon"
          :isTagSelected="selectedItem && selectedItem._id === item._id"
          @updatePolygon="item.polygon = $event"
          @selectedPolygon="polygonSelected($event)"
        />
      </div>

      <div v-for="item in subsectionMarkers" :key="item._id">
        <tag-basic
          v-bind="item.marker"
          :pos="{ 
            x: item.marker.x,
            y: item.marker.y
          }"
          :label="{ en: item.label + '<br>now selling' }"
          :style="`transform: rotate(${item.marker.rotation}deg);`"
          @pointerdown.native="onMouseDown(item, $event)"
          @pointerup.native="onMouseUp($event)"
          @pointermove.native="onMouseDrag($event)"
        />

        <polygon-shape
          :polygon="item.polygon"
          :scale="scale"
          :isHighlighted="highlightedPolygon === item.polygon"
          :isSelected="selectedPolygon === item.polygon"
          :isTagSelected="selectedItem && selectedItem._id === item._id"
          @updatePolygon="item.polygon = $event"
          @selectedPolygon="polygonSelected($event)"
        />
      </div>

      <div v-for="item in livingOptionMarkers" :key="item._id">
        <tag-basic
          v-bind="item.marker"
          :pos="{ 
            x: item.marker.x,
            y: item.marker.y
          }"
          :label="{ en: item.label + '<br>now selling' }"
          :style="`transform: rotate(${item.marker.rotation}deg);`"
          @pointerdown.native="onMouseDown(item, $event)"
          @pointerup.native="onMouseUp($event)"
          @pointermove.native="onMouseDrag($event)"
        />

        <polygon-shape
          :polygon="item.polygon"
          :scale="scale"
          :isHighlighted="highlightedPolygon === item.polygon"
          :isSelected="selectedPolygon === item.polygon"
          :isTagSelected="selectedItem && selectedItem._id === item._id"
          @updatePolygon="item.polygon = $event"
          @selectedPolygon="polygonSelected($event)"
        />
      </div>

      <reference-tag-slider 
        v-if="referenceTagsSlider.length > 0"
        :isVertical="true"
        :scaledAmount="scale"
        :tagRef="referenceTagsSlider"
        @click.native.stop="{}"
      />

      <draggable-elements-menu v-if="selectedItem">
        <div class="group-inline">
          <label>Font Size</label>
          <input class="group-inline__input" type="number" step="0.01" v-model.number="selectedItem.marker.fontSize">
        </div>
        
        <div v-if="selectedItem.marker && selectedItem.marker.padding" class="group-block">
          <label>Padding</label>
          <div class="group-block__inputs">
            <div class="group-block__input-wrapper">
              top <input class="group-block__input" type="number" step="0.01" v-model="selectedItem.marker.padding[0]">
            </div>
            <div class="group-block__input-wrapper">
              bottom <input class="group-block__input" type="number" step="0.01" v-model="selectedItem.marker.padding[2]">
            </div>
            <div class="group-block__input-wrapper">
              left <input class="group-block__input" type="number" step="0.01" v-model="selectedItem.marker.padding[3]">
            </div>
            <div class="group-block__input-wrapper">
              right <input class="group-block__input" type="number" step="0.01" v-model="selectedItem.marker.padding[1]">
            </div>
          </div>
        </div>

        <div class="group-inline">
          <label>Text Colour</label>
          <div
            class="btn-colour"
            :style="{ backgroundColor: selectedItem.marker.textColour }"
            @click="colourWidget === 'textColour' ? colourWidget = '' : colourWidget = 'textColour'"
          ></div>
        </div>
        <div v-if="colourWidget === 'textColour'" class="mb-2 mx-2">
          <verte display="widget" picker="square" model="rgb" v-model="selectedItem.marker.textColour"></verte>
        </div>

        <div class="group-inline">
          <label>Background Colour</label>
          <div
            class="btn-colour"
            :style="{ backgroundColor: selectedItem.marker.backgroundColour }"
            @click="colourWidget === 'backgroundColour' ? colourWidget = '' : colourWidget = 'backgroundColour'"
          ></div>
        </div>
        <div v-if="colourWidget === 'backgroundColour'" class="mb-2 mx-2">
          <verte display="widget" picker="square" model="rgb" v-model="selectedItem.marker.backgroundColour"></verte>
        </div>
        
        <div class="group-inline">
          <label>Tail Position</label>
          <select v-model="selectedItem.marker.tailPosition" class="group-inline__input">
            <option value="none">No Tail</option>
            <option value="bottom-left">Bottom Left</option>
            <option value="bottom-center">Bottom Center</option>
            <option value="bottom-right">Bottom Right</option>
            <option value="top-left">Top Left</option>
            <option value="top-center">Top Center</option>
            <option value="top-right">Top Right</option>
            <option value="left-center">Left Center</option>
            <option value="right-center">Right Center</option>
          </select>
        </div>
        <div class="group-inline">
          <label>Tail Height</label>
          <input class="group-inline__input" type="number" v-model.number="selectedItem.marker.tailHeight">
        </div>

        <div class="group-inline">
          <label>Rotation</label>
          <input class="group-inline__input" type="number" v-model.number="selectedItem.marker.rotation">
        </div>

        <div class="group-inline">
          <label>Opacity</label>
          <input class="group-inline__input" type="number" step="0.01" max="1" min="0" v-model.number="selectedItem.marker.opacity">
        </div>

        <div class="group-inline">
          <label>Box Shadow</label>
          <input class="group-inline__input" type="checkbox" v-model.number="selectedItem.marker.boxShadow">
        </div>

        <div class="group-inline mt-auto">
          <button class="btn btn-sm btn-danger mx-auto" @click="reset">Reset</button>
        </div>
      </draggable-elements-menu>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DraggableElementsMenu from '@/components/DraggableElementsMenu'
import PolygonShape from '@/components/PolygonShape'
import TagBasic from '@/components/TagBasic'
import Verte from 'verte'
import 'verte/dist/verte.css'

export default {
  props: ['value', 'property-ids', 'subsection-ids', 'subsection-group-ids', 'living-option-ids', 'background-image'],

  components: {
    DraggableElementsMenu,
    PolygonShape,
    TagBasic,
    Verte,
  },

  data () {
    return {
      scale: 1,
      
      propertyDot: null,
      statusDot: null,
      selectedItem: null,

      highlightedPolygon: null,
      selectedPolygon: null,

      isTagSelected: false,

      isMarkerDetailsActive: false,

      colourWidget: ''
    }
  },

  computed: {
    ...mapGetters([
      'activeLanguage',
      'page',
      'properties',
      'subsections',
      'subsectionGroups',
      'livingOptions',
    ]),

    tags: {
      get () {
        return this.value
      }, 
      set (tags) {
        this.$emit('input', tags)
      }
    },

    propertyMarkers () {
      if (this.propertyIds) {
        return this.properties.filter(property => this.propertyIds.includes(property._id))
      }

      return []
    },

    subsectionMarkers () {
      if (this.subsectionIds) {
        return this.subsections.filter(subsection => this.subsectionIds.includes(subsection._id))
      }

      return []
    },

    subsectionGroupMarkers () {
      if (this.subsectionGroupIds) {
        return this.subsectionGroups.filter(subsectionGroup => this.subsectionGroupIds.includes(subsectionGroup._id))
      }

      return []
    },

    livingOptionMarkers () {
      if (this.livingOptionIds) {
        return this.livingOptions.filter(option => this.livingOptionIds.includes(option._id) && option.active)
      }

      return []
    },

    bgStyle () {
      if (this.scale) {
        return {
          ['font-size']: (20 * this.scale) + 'px',
          ['height']: (1960 * this.scale) + 'px',
        }
      }
      return {}
    },

    referenceTagsSlider () {
      if (this.tags) {
        let refTags = this.tags.filter(function(tag) {
          return tag.type == "reference"
        })

        refTags = _.orderBy(refTags, ['pos.x'], ['asc'])

        if (this.dir === 'rtl') {
          let startingIndex = refTags.length

          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = startingIndex--
            }
          })
          return refTags
        } else {
          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = index + 1
            }
          })
          return refTags
        }
      } else {
        return []
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.getScaledAmount)
    this.$nextTick(() => {
      this.getScaledAmount()
    })

    // add event listener for esc key
    window.addEventListener('keydown', this.onKeyDown)
  },

  methods: {
    onKeyDown (e) {
      if (this.statusDot) {
        e.preventDefault()

        switch (e.keyCode) {
          case 27:
            this.clearSelection()
            break

          case 38:
            this.statusDot.y--
            break

          case 40:
            this.statusDot.y++
            break

          case 37:
            this.statusDot.x--
            break

          case 39:
            this.statusDot.x++
            break
        
          default:
            break
        }
      }
    },

    getScaledAmount() {
      // default size based on 3190x1960 - scale = 1
      if (this.$refs.canvasWrapper) {
        let canvasWidth = this.$refs.canvasWrapper.getBoundingClientRect().width
        let scaled = canvasWidth / 3190
        this.scale = scaled
      }
    },

    clearSelection () {
      this.propertyDot = null
      this.statusDot = null
      this.selectedItem = null

      this.highlightedPolygon = null
      this.selectedPolygon = null

      this.isTagSelected = false
    },

    onMouseDrag(event) {      
      if (this.propertyDot) {
        this.propertyDot.x = Math.round(this.propertyDot.x + event.movementX / this.scale)
        this.propertyDot.y = Math.round(this.propertyDot.y + event.movementY / this.scale)
      }
    },

    addTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.tags.push(res.data)
      })
    },

    openAssetLibrary(context, assetSelected) {
      this.$emit('openAssetLibrary', context, assetSelected)
    },

    onMouseDown (item, e) {
      e.target.setPointerCapture(e.pointerId)

      this.clearSelection()
      
      this.selectedItem = item
      this.propertyDot = item.marker
      this.statusDot = item.marker
      this.highlightedPolygon = item.polygon
    },
    
    onMouseUp (e) {
      e.target.releasePointerCapture(e.pointerId)
      this.propertyDot = null
    },

    updateActiveDotPosition (e) {
      if (this.propertyDot) {
        this.propertyDot.pos.x = Math.round(this.propertyDot.pos.x + e.movementX / this.scale)
        this.propertyDot.pos.y = Math.round(this.propertyDot.pos.y + e.movementY / this.scale)
      }
    },

    updatePolygon (targetItem, polygon) {
      targetItem.polygon = polygon
    },

    polygonSelected (e) {
      this.clearSelection()
      this.selectedPolygon = e
    },

    reset () {
      if (window.confirm("Are you sure you want to reset the marker styles?")) {
        this.selectedItem.marker = {
          fontSize: 0.75,
          padding: [0.5, 1.1, 0.4, 1.1],
          textColour: '#000000',
          backgroundColour: '#FFFFFF',
          rotation: 0,
          boxShadow: true,
          tailHeight: 4,
          tailPosition: 'none',
          x: this.selectedItem.marker.x,
          y: this.selectedItem.marker.y,
        }
      }
    }
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('resize', this.getScaledAmount)
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
@import '@/styles/_mixins.scss';

.markers-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}

.dot {
  border-radius: 50%;
  background-color: red;
  cursor: pointer;
  pointer-events: all;
  position: absolute;
  font-size: .75em;
  height: 3em;
  width: 3em;
  user-select: none;
  opacity: 1;
  transform: translate(-.25em, -.4em);
  z-index: 6;

  &__label {
    background: #fff;
    bottom: 3.5em;
    left: 50%;
    opacity: 0;
    padding: .5em;
    pointer-events: none;
    position: absolute;
    transform: translateX(-50%);
    width: max-content;
  }

  &:hover {
    .dot__label {
      opacity: 1;
    }
  }

  &__status {
    background-color: rgba($white, 0.7);
    color: $black;
    font-size: 16px;
    padding: 10px;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 6;

    input.form-control  {
      display: inline-block;
      padding: 0 5px;
      width: 60px;
    }
  }
}
</style>
